import { WIDOW_SIZE, type ShelemMove, type ShelemView } from "@bing/engine-shelem";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Avatar } from "../components/Avatar";
import { Card } from "../components/Card";
import { GameIcon } from "../components/GameIcon";
import { DIAMOND_SEAT_EDGES, seatEdge, ThrownCard } from "../game/card-motion";
import { DealerMarker } from "../game/dealer-marker";
import { atX, atY, OWN_AVATAR } from "../game/felt-geometry";
import { HakemChip, HakemNoticePill } from "../game/hakem-chip";
import {
  HAND_CARD_SCALE,
  HAND_FAN_BOX_H,
  handCardPlacement,
  handFanBudget,
  handFanLayout,
} from "../game/hand-fan";
import { sortHand } from "../game/hand-sort";
import { LIFTED_EXTRA_PX, LIFTED_TRANSITION, useLiftToPlay } from "../game/lift-to-play";
import { TurnTimerSlot, type TableTurnTimer } from "../game/online-lifecycle";
import { presetAvatarFor } from "../game/seat-avatar";
import { allTwelve, signedScore } from "../game/shelem-hand-result";
import { shelemSeatFallback } from "../game/shelem-seat-label";
import {
  resolveSeatAvatar,
  resolveSeatName,
  type SeatAvatar,
  type SeatIdentity,
  type SeatPlayer,
} from "../game/seat-identity";
import { noticeForSeat, type SeatNotice } from "../game/seat-notice";
import {
  pointsCollected,
  useHandEndMoments,
  useKafMoments,
  useRedealMoments,
  useTrickPoints,
  useTrumpMoments,
} from "../game/shelem-moments";
import { useAuctionSfx } from "../game/shelem-sfx";
import { StatusPill, TABLE_BTN } from "../game/table-parts";
import {
  AVATAR_CORNER,
  TableSeat,
  TURN_GLOW_STEADY_TIGHT,
  turnGlowSettle,
  type TableSeatProps,
} from "../game/TableSeat";
import { TrumpSign } from "../game/trump-sign";
import { ShelemSideSeat } from "./shelem-side-seat";
import { useCompactTable } from "../game/use-compact-table";
import { useFrameKey } from "../game/use-frame-key";
import {
  isVirginDeal,
  lastPlayOf,
  shownTrick,
  useCardLandSfx,
  useDealSfx,
  useTrickResultSfx,
} from "../game/use-table-sfx";
import { TRICK_HOLD_MS, useTrickHold, type TrickHold } from "../game/use-trick-hold";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { getLiftToPlay } from "../settings/prefs";
import { playSfx, type SfxName } from "../sound/sfx";
import {
  AuctionBadge,
  AuctionCentre,
  auctionCentreFits,
  auctionCentrePlacement,
  auctionPanelHeight,
  BidRow,
  NAME_LINE_PX,
  RedealNotice,
  seatRootHeight,
  type BidChoice,
} from "./shelem-auction";
// the trump's pip is `trumpPip`: an inline `<SuitGlyph />`, never the Unicode ♠♥♦♣ (it moved beside the
// contract parts it is drawn in — P1 Task 6)
import {
  ContractHud,
  hudShortForm,
  KafPickupLine,
  TrumpAnnounce,
  trumpPip,
} from "./shelem-contract";
import { PARTNER_TOP_IN_FELT, SIDE_NAME_MAX, shelemSideNameMaxPx } from "./shelem-felt";
import { TrickPointsChip } from "./shelem-felt-moments";
import { HandEndCard, handEndCardPlacement } from "./shelem-hand-end";
import {
  KafDiscardSweep,
  kafCornerPlacement,
  KafPickupFlight,
  WidowCorner,
  type KafCorner,
} from "./shelem-kaf";

// The auction centre's placement is pinned by `shelem-auction-centre.test.tsx` through this module.
export { auctionCentrePlacement, seatRootHeight } from "./shelem-auction";

/* ------------------------------------------------------------------ *
 * شلم (Shelem) — the table on the shared frame (P1 Task 3), built the way حکم's (`HokmTable`) and
 * بی‌دل's (`HeartsTable`) are:
 *   - the FRAME: one 440 px column, `--game-h` tall, safe areas through `max(env(), var())`;
 *   - the HEADER: one 40 px band at 100 % — the exit, the chat toggle's footprint on an online table,
 *     «شلم» over the hand number (only that — card-67 B1; above 100 % the header grows to keep it on
 *     its own line, uncut), and a FIXED-width score box: the two totals toward the target;
 *   - the FELT: حکم's gold-ringed `rounded-[40px]` cloth, plain (card-67 B5: no star), the trick in
 *     clamped slots that keep every card off every seat, حکم's trump sign at its top-left (card-67
 *     B2) and حکم's gold notice at its foot (card-67 B1);
 *   - the SEATS: the shared `TableSeat` — the partnership by colour, the حاکم's crown chip above its
 *     avatar, the dealer's marker on its corner, the auction's bid / pass under its name, a seat's
 *     notice in its name's place, the host's «+» on a bot;
 *   - YOUR STRIP: your avatar, your name, then the status pill and the turn timer's ONE slot;
 *   - YOUR HAND: the shared fan, sorted, fitted to the screen — 16 cards at 320 px — and played by a
 *     tap (or, with «تأیید قبل از بازی کارت», a lift then a tap on the table).
 * THE AUCTION (P1 Task 4) — true for whoever reads it: the felt's one-line statement «بالاترین: ۱۳۵ —
 * {name}» and the face-down کف in the felt's corner for everyone; a seat's bid or «پاس» as a chip
 * ABOVE its avatar (never under its name, where it reached the row and the centre on a short felt);
 * the bidding ROW — every seat's slot in two halves, ours and theirs (card-67 C3), the ladder of legal
 * raises with the cheapest preselected, the separate gold «شلم» call, «پاس» two tiles wide and «ثبت»
 * (C1) — for the seat to bid ALONE, opening ABOVE the strip so the strip and the timer never move; the
 * حاکم's «کنار بگذار» one `h-9` button under the contract bar, «ثبت»'s size (C2).
 * THE CONTRACT (P1 Task 5): the کف's four fly from the felt to the حاکم — face-up into your hand and
 * marked when it is you, face-down to its seat for the others, who read «{name} زمین را برداشت» —
 * then the four laid down go to its pile; the حاکم's first card lands and «حکم: ♠ — {name}» names
 * trump (the first place the suit appears — then the felt's top-left sign keeps it); the bottom bar
 * above your strip says the contract and our points this hand, in fixed slots (`shelem-contract.tsx`,
 * card-67 B3; `game/shelem-moments.ts`). The hand is held through each moment, as the engine's beats
 * are. Every control is still derived from the server's `legal` list.
 * THE END OF A TRICK AND OF A HAND (P1 Task 6): every finished trick stays on the felt for its whole
 * hold + collect — حکم's and بی‌دل's `useTrickHold`, the carried 12th trick read with the LAST hand's
 * trump; a point card's «+۱۰» rides the collect and the HUD counts the trick as it is collected; once
 * a hand's 12th trick is collected its cue sounds (made / missed / all twelve) and the hand-end card
 * is read (P1 Task 7, `shelem-hand-end.tsx`): the کف revealed, the contract settled — all twelve (a
 * sweep, a «شلم» call made) wear its gold banner line, Task 6's felt-wide banner folded in.
 * ------------------------------------------------------------------ */

// A seat with no known player reads its positional label — ONE implementation with the end card
// (`game/shelem-seat-label.ts`, P1 Task 7); `PARTNER_NAME` stays importable from here for the tests.
export { PARTNER_NAME } from "../game/shelem-seat-label";

/** شلم is a fixed 2-v-2 partnership: a seat is on your team iff it shares your seat's parity. */
const onMyTeam = (you: number, seat: number): boolean => seat % 2 === you % 2;

/** YOUR avatar's on-turn ring in the strip (card-a7 B6): `TURN_GLOW`'s gold ring with a TIGHT glow —
 *  the strip's own `turn-glow-pulse-tight` peaks at 8 px, so neither your glow nor the controls'
 *  above the strip (the bidding row, «کنار بگذار») ever lies on the other (11 px apart). */
const OWN_TURN_GLOW = "0 0 6px 1px rgb(var(--c-secondary) / .4), 0 0 0 2px rgb(var(--c-secondary))";

/** The panel's `gap-2` between its parts — the bidding row and your strip among them. */
const PANEL_GAP_PX = 8;

/** A seat's ring off turn: your team green, the opponents red — حکم's partnership look (its
 *  `teamRing`; `shelem-layout.test.tsx` pins the two tables' rings equal). The gold turn-glow
 *  (`TableSeat`'s `TURN_GLOW`) always wins over it. */
function teamRing(my: boolean): string {
  return my
    ? "0 0 0 2px rgb(var(--c-primary)), 0 4px 8px rgba(0,0,0,.4)"
    : "0 0 0 2px rgb(var(--c-danger-bluff)), 0 4px 8px rgba(0,0,0,.4)";
}

/** A seat's name box SKIN (`TableSeat` draws its frame and its truncation): حکم's caption-sized box in
 *  the seat's team colour — the box the trick slots' K below are measured on. */
const seatNameSkin =
  (my: boolean) =>
  (compactPad: string | null): string =>
    `${compactPad ?? "px-2"} py-0.5 font-caption text-caption shadow-md ${
      my
        ? "border-primary/70 bg-surface-container/90 text-primary"
        : "border-error/70 bg-surface-container/90 text-error"
    }`;

const TOP_NAME_MAX = "92px";

/*
 * THE TRICK'S SLOTS — حکم's clamps (`HokmTable`'s TRICK_SIDE / TRICK_TOP / TRICK_DROP / TRICK_OWN),
 * the numbers kept HERE because each table keeps the numbers it measured (`felt-geometry.ts`). They
 * are حکم's because the seats are: the shared `TableSeat` in حکم's places with حکم's name box, so the
 * two things the K encode are the same — the widest box a side seat draws («در حال اتصال», 72.3 px
 * zoomed / 80.3 regular, 4 px inside the felt) and the partner's name box bottom (86 / 96 px below the
 * felt's top). Each clamp's ceiling is the roomy offset (64 / 30 / 64); a small felt pulls the cards
 * in, then drops the whole diamond into the empty band below, so no card lands under a seat (W7: the
 * old table's z-10 cards sat under the z-20 avatars). `50%` inside `left` / `top` IS half the felt —
 * nothing to measure. The auction's bid / pass badges and a card are on screen together only while
 * the carried 12th trick is collected (its 950 ms hold, P1 Task 6): any bid or pass sweeps it off the
 * frame (the engine's `completedTrick: []`) and no bot speaks within the hold (the engine's 1100 ms
 * beat) — only a human's word inside that second can raise a badge over it.
 */
const TRICK_SIDE = {
  compact: "clamp(36px, calc(50% - 106px), 64px)",
  regular: "clamp(36px, calc(50% - 114px), 64px)",
} as const;
/*
 * …AND THE PARTNER'S K GROWS WITH THE TEXT ZOOM (P1 Task 5). The 129 / 139 are the partner's name box
 * bottom at 100 % (86 / 96) plus the card's reach; the one part of that box a WebView text zoom grows
 * is its caption line (16 px × zoom), so K = 129 / 139 + 16·(zoom − 1) — at 100 % exactly حکم's K.
 * Under a 130 % zoom the box is 4.8 px taller, and a felt shorter than 2K (320×568 under the contract
 * HUD: 216.6 px) parked the side cards and the partner's own card that much INTO the partner's name
 * (the probe's `seat-trick` 54 there). The side offset needs no zoom term: `SIDE_NAME_MAX` caps the
 * side box whatever the zoom.
 */
const TRICK_K = { compact: 129, regular: 139 } as const;
const CAPTION_LINE_PX = 16;
/** The trick's four slot offsets on this screen at this text zoom (the clamps above, K zoomed). */
function trickSlots(
  compact: boolean,
  zoom: number,
): { side: string; top: string; drop: string; own: string } {
  const k = `${Number(((compact ? TRICK_K.compact : TRICK_K.regular) + CAPTION_LINE_PX * (zoom - 1)).toFixed(1))}px`;
  const drop = `clamp(0px, calc(${k} - 50%), calc(50% - 34px))`;
  return {
    side: compact ? TRICK_SIDE.compact : TRICK_SIDE.regular,
    top: `clamp(0px, calc(50% - ${k}), 30px)`,
    drop,
    own: compact
      ? `clamp(0px, calc(50% - ${drop} - 34px), clamp(64px, calc((174.2px - 50vw) * 100 + 64px), 75px))`
      : `clamp(0px, calc(50% - ${drop} - 34px), 64px)`,
  };
}

/*
 * THE PLAYED CARDS GROW WHERE THE FELT HAS ROOM (B3 item 1, operator 2026-10-20: «can the cards on
 * the table be bigger, without overlapping anything else, on every device size?») — حکم's rule for
 * its diamond (`HokmTable`'s `trickCardScale`, 2026-10-04: «the same size as in my hand — not
 * bigger»), on شلم's seats. The whole diamond is drawn at a scale `s`, the largest at which:
 *   ACROSS  a side card's outer reach (its offset + 27.7 px of a 4°-tilted card, at `s`) stops 2 px
 *           short of the widest box a side seat draws — a long name at شلم's SIDE_NAME_MAX, which
 *           tracks the width (`min(50vw, 220px) − 110px`: its inner edge 94 px from the centre from
 *           302 to 404 px), or «در حال اتصال» as the clamps above encode it (72.3 / 80.3 px) — and
 *           the side card, drawn IN as the card grows, never reaches the centre column's cards (the
 *           partner's and yours, 26.8 px half-wide at 3°): the binding term from 302 to 404 px;
 *   DOWN    the diamond, from the partner's card top (30 + 40.8 px above the slot line, at `s`) to
 *           your card's bottom (64 + 31.8 px below it), fits between the partner's name box (the
 *           K's 86 / 96 px, zoomed) and the felt's bottom edge, 2 px clear of each;
 *   CAP     never above your hand's scale (HAND_CARD_SCALE).
 * Where `s` is 1 nothing changes: the slots keep the CSS clamps above, byte for byte — every tight
 * size the layout matrix and its ruled list verified (300×667 ×1.5) draws exactly what it drew.
 */
const TRICK_REACH = { side: 27.7, centreHalf: 26.8, up: 40.8, ownDown: 31.8 } as const;
const TRICK_ROOMY = { side: 64, top: 30, own: 64 } as const;
const SEAT_BOX = {
  compact: { notice: 72.3, topNameBottom: 86 },
  regular: { notice: 80.3, topNameBottom: 96 },
} as const;
const FELT_CARD_GAP = 2;

/** How far from the felt's centre a side card's outer edge may reach (px). */
function sideReachMax(feltW: number, compact: boolean): number {
  const seat = compact ? SEAT_BOX.compact : SEAT_BOX.regular;
  // the side seat's box starts 4 px inside the felt (16 px past the table's edge)
  const widest = Math.max(seat.notice, shelemSideNameMaxPx(feltW));
  return feltW / 2 - 4 - widest - FELT_CARD_GAP;
}

/** The played cards' scale on a felt of this size at this text zoom — 1 where there is no room. */
export function shelemTrickScale(
  feltW: number,
  feltH: number,
  compact: boolean,
  zoom: number,
  cap: number = HAND_CARD_SCALE,
): number {
  if (!(feltW > 0 && feltH > 0)) return 1;
  const seat = compact ? SEAT_BOX.compact : SEAT_BOX.regular;
  const nameBottom = seat.topNameBottom + CAPTION_LINE_PX * (zoom - 1);
  // ACROSS: the side card drawn in to its reach, its inner edge clear of the centre column's
  const across = sideReachMax(feltW, compact) / (TRICK_REACH.side * 2 + TRICK_REACH.centreHalf);
  const tall = TRICK_ROOMY.top + TRICK_REACH.up + TRICK_ROOMY.own + TRICK_REACH.ownDown;
  const down = Math.min(
    (feltH - nameBottom - 2 * FELT_CARD_GAP) / tall,
    (feltH / 2 - FELT_CARD_GAP) / (TRICK_ROOMY.own + TRICK_REACH.ownDown),
  );
  const s = Math.floor(Math.min(cap, across, down) * 1000) / 1000;
  return s > 1 ? s : 1;
}

/** The slots' offsets (px) for a GROWN diamond (`scale` > 1): the roomy offsets at `scale` — the side
 *  one drawn in to the room across — and the drop that keeps the partner's card 2 px under the
 *  partner's name. */
export function grownShelemTrickSlots(
  feltW: number,
  feltH: number,
  compact: boolean,
  zoom: number,
  scale: number,
): { side: number; top: number; drop: number; own: number } {
  const seat = compact ? SEAT_BOX.compact : SEAT_BOX.regular;
  const nameBottom = seat.topNameBottom + CAPTION_LINE_PX * (zoom - 1);
  const r2 = (v: number): number => Math.round(v * 100) / 100;
  const top = TRICK_ROOMY.top * scale;
  const drop = Math.max(0, nameBottom + FELT_CARD_GAP + TRICK_REACH.up * scale + top - feltH / 2);
  return {
    side: r2(
      Math.min(TRICK_ROOMY.side * scale, sideReachMax(feltW, compact) - TRICK_REACH.side * scale),
    ),
    top: r2(top),
    drop: r2(drop),
    own: r2(TRICK_ROOMY.own * scale),
  };
}

/*
 * THE CHAT TOGGLE'S FOOTPRINT — `TableChat`'s closed toggle is `position: fixed`, 40 × 40, 64 px in
 * from the column's inline-start edge on the header's top: exactly the slot after the exit (16 px of
 * the header's `px-4` + the exit's 40 + the row's `gap-2`). Every ONLINE table keeps it free from its
 * first frame (W5: the toggle used to land on «شلم»); solo has no chat, and «شلم» centres between the
 * box and the exit. `shelem-layout.test.tsx` pins it against TableChat's own source.
 */
const CHAT_TOGGLE_PX = 40;

/*
 * THE SCORE BOX NEVER CHANGES WIDTH (حکم's rule — operator, 2026-10-03). Vazirmatn's digits are not
 * tabular (۳ is 12.22 px at headline-sm, ۱ 5.56), so every value sits in a box just over the widest it
 * can hold, centred — and the boxes are in EM of the value's own size, so the box is ONE width
 * whatever the score AND grows with a WebView text zoom (a px box would not: at ×1.5 a four-digit
 * total spilled 12 px past it). Measured off Vazirmatn's own advance widths (the woff's `hmtx`, the
 * numbers the browser lays out — they reproduce حکم's «۳ = 12.22 px» and «ما = 9.55 px»):
 *   the totals   «−۱۳۳۵» is the widest value under ±2000: 49.60 px at headline-sm (18 px / 700) —
 *                2.756 em. «−۳۳۳۵» (a match gone very wrong) is 56.26 px and spills into the box's
 *                gaps, never onto a neighbour; the harness's widest, «−۱۹۸۰», is 46.29;
 *   the target   «۱۲۰۰» under «تا»: 31.25 px — 1.736 em («۶۶۰» is narrower).
 * The minus is U+2212, in a `dir="ltr"` span: «−۱۲۰» reads with its sign on the LEFT, as a number
 * does — bare in this RTL row the sign would land on the right («۱۲۰−»).
 * In the COMPACT form the values drop to 14 px bold (the same em, 38.6 / 24.3 px) and the gaps
 * tighten: the box is then 139.6 px wide, which leaves «شلم» (38.7 px at headline-md) 44.5 px at
 * 320 px with the chat toggle's footprint.
 *
 * THE FORM IS MEASURED, NOT A BREAKPOINT (fix round 1): the em boxes grow with a WebView text zoom
 * while the band's px parts (the exit, the chat footprint, the gaps) do not, so `max-[379px]` cannot
 * see the zoom — at 130 % the regular box is 231.9 px and left «شلم» 16 px at 384 (the operator's
 * phone), the title painting over the box and the chat slot on every width ≤ 390 (`header-header`
 * 598 in Task 3's probe). `scoreBoxForm` reads the table's laid-out width and the zoom the page is
 * drawn at (`useLaidOut`) and picks ONE of three forms, each a fixed width per screen and zoom:
 *   regular   the target and the two totals at headline-sm — from حکم's 380 px up, while the title
 *             keeps its air (below);
 *   compact   the same three at 14 px bold, the gaps tightened — 170.1 px at 130 %: «شلم» has 77.9
 *             at 384, 83.9 at 390, 53.9 at 360;
 *   tight     the two totals ALONE (no «تا ۱۲۰۰»), the compact look — where even the compact box
 *             would leave the title short (320 px at 130 %: 13.9 px; the tight box leaves 58.5).
 *             «شلم» is NEVER cut (controller, fix round 1: an ellipsis where the game's name stands
 *             reads as a glitch; Task 5's contract display shows the target anyway).
 * A form fits while the title keeps at least HALF a gap (4 px, TITLE_AIR_PX) of air from the chat
 * slot and the box — it may lean 4 px into each `gap-2` beside its column, no more. The ONLINE row
 * is assumed on solo too, so the form never reads the state: the box is ONE width per screen and
 * zoom whatever the scores (the probe's «spread 0»). Where no form fits (256 px at 130 %: the tight
 * box, the exit, the chat slot and the gaps outrun the 224 px row by 5.5 px) the tight form stands
 * and the title overlaps — a best-effort size.
 */
const SCORE_EM_TOTAL = 2.76;
const SCORE_EM_TARGET = 1.74;
const SCORE_EM = { total: `${SCORE_EM_TOTAL}em`, target: `${SCORE_EM_TARGET}em` } as const;
type ScoreForm = "regular" | "compact" | "tight";
/**
 * The three forms: the box's classes (its gaps and padding), the values' size class (the family is
 * Vazirmatn's), whether the target column is drawn — and the arithmetic of the box's width at a
 * text zoom: `chromePx` (the paddings, the gaps, the 1 px rules — rem-based, never zoomed) + the
 * values' em boxes (`em`: 1.74 + 2 × 2.76 with the target, 2 × 2.76 without) × `valuePx` × zoom.
 */
const SCORE_FORM: Record<
  ScoreForm,
  { box: string; value: string; target: boolean; chromePx: number; valuePx: number; em: number }
> = {
  regular: {
    box: "gap-2.5 px-2.5",
    value: "font-headline-sm text-headline-sm",
    target: true,
    chromePx: 2 * 10 + 4 * 10 + 2, // px-2.5 ×2, four gap-2.5s, two rules
    valuePx: 18,
    em: SCORE_EM_TARGET + 2 * SCORE_EM_TOTAL,
  },
  compact: {
    box: "gap-1.5 px-1.5",
    value: "font-headline-sm text-body-md font-bold",
    target: true,
    chromePx: 2 * 6 + 4 * 6 + 2, // px-1.5 ×2, four gap-1.5s, two rules
    valuePx: 14,
    em: SCORE_EM_TARGET + 2 * SCORE_EM_TOTAL,
  },
  tight: {
    box: "gap-1.5 px-1.5",
    value: "font-headline-sm text-body-md font-bold",
    target: false,
    chromePx: 2 * 6 + 2 * 6 + 1, // px-1.5 ×2, two gap-1.5s, one rule
    valuePx: 14,
    em: 2 * SCORE_EM_TOTAL,
  },
};
/** حکم's breakpoint: below 380 px the box is never regular, whatever the zoom («so «حکم» keeps its room»). */
const SCORE_REGULAR_MIN_WIDTH = 380;
/** The header row's px parts around the title and the box: `px-4` twice, the exit's `w-10`, the chat
 *  toggle's footprint (CHAT_TOGGLE_PX — counted on solo too, see scoreBoxForm), three `gap-2`s. */
const HEADER_GAP_PX = 8;
const HEADER_PX = 2 * 16 + 40 + CHAT_TOGGLE_PX + 3 * HEADER_GAP_PX;
/** The air «شلم» keeps from the chat slot and the box: half a gap — it may lean into the other half. */
const TITLE_AIR_PX = HEADER_GAP_PX / 2;
/** «شلم» at headline-md: ش initial + ل medial + م final, 38.71 px at 22 px off Vazirmatn's `hmtx` —
 *  1.7596 em, held as the em the layout test re-derives (`shelem-layout.test.tsx` §4). */
export const TITLE_EM = 1.76;
const TITLE_PX = 22;

/** A form's box width in CSS px at a text `zoom` (see SCORE_FORM). */
function scoreBoxWidth(form: ScoreForm, zoom: number): number {
  const f = SCORE_FORM[form];
  return f.chromePx + f.em * f.valuePx * zoom;
}

/**
 * The score box's form on a table `width` px wide, drawn at a text `zoom` (1 = 100 %): regular from
 * حکم's 380 px up while the ONLINE row — the exit, the chat footprint, the three gaps — leaves «شلم»
 * its width beside the regular box with half a gap of air on each side; else compact on the same
 * test; else the two totals (tight). The state never enters (solo has no chat slot but draws the
 * same box): one width per screen and zoom.
 */
/**
 * …and where even the two totals leave «شلم» less than its width beside the box and the chat slot —
 * 300 px under a 150 % zoom (Task 9b: the title over the box AND the slot by 9.5 px in every state) —
 * the TITLE yields: read by assistive tech, not painted (`sr-only`). The same arithmetic as the box's
 * forms; every other size and zoom keeps it (the controller's ruling, 2026-10-13).
 */
export function titleShown(width: number, zoom: number): boolean {
  // the title may lean into the WHOLE gaps beside it (centred in its column, it touches neither box):
  // at 300 × 1.5 the two totals (140.9 px) + «شلم» (58.1) need 199 of the 180 there — 9.5 px into each
  // neighbour, as measured; 320 × 1.5 holds them with a pixel to spare
  const room = width - HEADER_PX + 2 * HEADER_GAP_PX;
  return scoreBoxWidth("tight", zoom) + TITLE_EM * TITLE_PX * zoom <= room;
}

export function scoreBoxForm(width: number, zoom: number): ScoreForm {
  const room = width - HEADER_PX + 2 * (HEADER_GAP_PX - TITLE_AIR_PX);
  const title = TITLE_EM * TITLE_PX * zoom;
  const fits = (form: ScoreForm): boolean => scoreBoxWidth(form, zoom) + title <= room;
  if (width >= SCORE_REGULAR_MIN_WIDTH && fits("regular")) return "regular";
  return fits("compact") ? "compact" : "tight";
}

// A total reads in the box as `signedScore` draws it (`game/shelem-hand-result.ts`, shared with the
// hand-end card and the end card since P1 Task 7): Persian digits, a true minus sign (see SCORE_EM).

/*
 * YOUR HAND, FITTED (W6: at 320 px the old fan ran off both edges and covered your name). The shared
 * fan (`hand-fan.ts`) at its 1.15 scale; its width from the table's own width (`handFanBudget`: the
 * widest fan whose tilted edge cards stay 3 px inside both edges — 271.8 px at 320, so 16 cards sit
 * 14.3 px apart and 12 sit 19.5; from 390 px up the 340 px every fan is capped at). Its edges arc and
 * tilt as a fan 5 steps out at most (`offCap`): a 16-card کف drawn uncapped would drop its edge cards
 * ~40 px below the fan's box, and even 12 cards (5.5 out) dip 15.4 px — past the panel's 14 px bottom
 * padding. At 5 the edge card's lowest corner is 8.5 px below the box (2 px of arc + 6.5 of its 13°
 * tilt): inside. A hand of 11 or fewer is drawn exactly as حکم draws it.
 */
const FAN_OFF_CAP = 5;

/** A number laid out on an element, read off its computed style — never a bounding box — and
 *  re-read whenever the element resizes; null until laid out (happy-dom, a hidden page) or while
 *  `read` gives no positive number. Read in a LAYOUT effect, so the first painted frame already
 *  draws what fits. `read` must be a stable function (module-level), or the effect re-subscribes. */
function useLaidOut(
  ref: { current: HTMLElement | null },
  read: (cs: CSSStyleDeclaration) => number,
  /** A primitive that changes when the element is about to change size in THIS commit: the effect
   *  re-measures in the same layout pass (a setState there lands before paint), where the
   *  ResizeObserver alone answers a frame late. */
  rev: number = 0,
): number | null {
  const [value, setValue] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || typeof window.getComputedStyle !== "function")
      return undefined;
    const measure = (): void => {
      const v = read(window.getComputedStyle(el));
      if (!(v > 0)) return;
      setValue((prev) => (prev === v ? prev : v));
    };
    measure();
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, read, rev]);
  return value;
}
/** The table's laid-out width (the fan keeps its 340 px cap until it is known). */
const laidOutWidth = (cs: CSSStyleDeclaration): number => parseFloat(cs.width);
/** `main`'s laid-out height — what the felt has between the header and the panel (the auction's
 *  centre is placed off it, `auctionCentrePlacement`). */
const laidOutHeight = (cs: CSSStyleDeclaration): number => parseFloat(cs.height);
/** The text zoom the page is drawn at — an Android WebView's textZoom, the phone's font scale on a
 *  client that does not pin it: a 12 px `text-caption` span computes to 12 × zoom (the probes'
 *  `text-zoom.cjs` asserts the same), read off the score box's «ما» caption. Unreadable = 100 %. */
const CAPTION_PX = 12;
const textZoomOf = (cs: CSSStyleDeclaration): number => parseFloat(cs.fontSize) / CAPTION_PX;

/**
 * THE TITLE BLOCK'S TWO LINES — «شلم» on its `leading-6` line (24 px) over the hand number (card-67 B1:
 * `shelemHandNumber`; the phase line before) on the caption's 16 px: 24 + 16 = the band's 40 px at
 * 100 %. A WebView text zoom grows both lines (a LENGTH line-height too): 52 px at ×1.3, 60 at ×1.5.
 * Until card-67 the band never grew, so above 100 % the second line was not drawn at all (P1 Task 9a:
 * squeezed into the band it was cut under the felt's ring). THE COORDINATOR'S RULING (card-67, on the
 * operator's 384×823 phone at ×1.3 / ×1.5 — «under the game name, only the hand number»): above 100 %
 * the HEADER GROWS just enough to hold «دست n» on its own line under «شلم», the number uncut — the
 * title's column is `min-h-10`, never a fixed 40, so the header is 52 px at ×1.3 and 60 at ×1.5 (the
 * felt gives those 12 / 20 px; in play the one-line bottom bar, card-67 B3, gave back more). The exit
 * and the score box stay on the band's top line with the fixed chat toggle (`self-start`).
 * UNCUT: the line never truncates; like «شلم» it may lean into the WHOLE gaps beside its column (it
 * never reaches the chat slot or the box). Its widest label in a match decides — «دست ۳۳», 3.7822 em of
 * the caption off Vazirmatn 400's hmtx (a match is 80 hands at most, `DEFAULT_MAX_HANDS`) — so on a
 * given screen and zoom the line is drawn for the whole match or not at all: where even that does not
 * fit beside the box (the tight box under a large zoom — 320 ×1.5, 300 ×1.5; 256 at 100 %), it is read
 * by assistive tech, not painted (`sr-only`, the title's way — wherever «شلم» yields, so does the line,
 * which is the wider). The operator's 384 px: ×1.3 holds it inside its column (59.0 of 77.9 px), ×1.5
 * leans 5.3 px into each 8 px gap (68.2 of 57.5). The zoom itself is read off the score box's «ما»
 * caption (`textZoomOf`), never off this line (the final review B m6).
 */
export const HAND_EM = 3.79;
export function handLineShown(width: number, zoom: number, form: ScoreForm): boolean {
  const column = width - HEADER_PX - scoreBoxWidth(form, zoom);
  return HAND_EM * CAPTION_PX * zoom <= column + 2 * HEADER_GAP_PX;
}

/**
 * THE TRUMP SIGN'S TOP (felt px) — حکم's 24 (card-67 B2: «top left, exactly like حکم»), raised only by
 * what keeps 2 px of air over the LEFT حاکم's «حاکم» chip, and never above 6 (card-67 D, the matrix: at
 * 300×667 under a 150 % zoom that chip, above its avatar on the short felt, reached 1.1 px into the sign
 * — 20 px there). The left seat's root is centred on the felt (a wrapped notice adds a line); the chip
 * sits 3 px over its ring, `9 × zoom + 4` tall (`HakemChip`: 9 px on `leading-none`, `py-0.5`); the sign
 * is 34 px (its 26 px pip disc in `py-1`) at every zoom. Unmeasured, or no حاکم on the left: 24.
 *
 * card-98 (the bottom bar's third slot takes a second line at 320 / 300 px under a 150 % zoom, so the
 * felt is ~23 px shorter in play there): the sign also clears a left seat that is NOT the حاکم where a
 * wrapped notice lifts that seat half a line into it (3.4 px into the sign at 320×568, 5.9 at 300×667)
 * — over its avatar's ring (the ring's 2 px spread, then 2 px of air), only as high as the felt's
 * rounded corner allows (`SIGN_CORNER_TOP`): where that is not enough (Telegram Desktop 307×472, the
 * felt 170 px), and with no notice, it keeps حکم's 24 as before. And where the حاکم's chip raises it into that corner (300×667 ×1.5: 9 px
 * down, the sign's box 1.8 px past the felt's ring), it steps RIGHT just far enough to keep 1 px inside
 * it (`trumpSignLeft`) — never at the floor (6), where the felt is too short to clear the chip at all
 * and the sign stays where card-67 D put it.
 */
export const TRUMP_SIGN_TOP = 24;
const TRUMP_SIGN_H = 34;
/** The sign's place across: 12 px in (`left-3`). */
export const TRUMP_SIGN_LEFT = 12;
const TRUMP_SIGN_FLOOR = 6;
/** The felt's corner radius (`rounded-[40px]`) and the air a box keeps inside its ring (1 px, the
 *  layout probes' `*-felt-edge` rule): the box's corner at (`left`, `top`) stays within `40 − 1` of the
 *  corner's centre (40, 40). */
const FELT_CORNER_R = 40;
const FELT_CORNER_AIR = 1;
const cornerReach = (at: number): number =>
  FELT_CORNER_R -
  Math.sqrt(Math.max(0, (FELT_CORNER_R - FELT_CORNER_AIR) ** 2 - (FELT_CORNER_R - at) ** 2));
/** The highest top the sign's box may take at its own 12 px in — 12.85, so 13. */
const SIGN_CORNER_TOP = Math.ceil(cornerReach(TRUMP_SIGN_LEFT));
/** The left seat's root's top on the felt (its root centred; a wrapped notice adds a line). */
const leftRootTop = (feltH: number, compact: boolean, zoom: number, leftNotice: boolean): number =>
  feltH / 2 - (seatRootHeight(compact, zoom) + (leftNotice ? NAME_LINE_PX * zoom : 0)) / 2;
/** card-67 D's raise over the left حاکم's chip, before its floor. */
const overChip = (feltH: number, compact: boolean, zoom: number, leftNotice: boolean): number =>
  Math.floor(leftRootTop(feltH, compact, zoom, leftNotice) - 3 - (9 * zoom + 4) - TRUMP_SIGN_H - 2);
export function trumpSignTop(
  feltH: number | null,
  compact: boolean,
  zoom: number,
  leftHakem: boolean,
  leftNotice = false,
): number {
  if (feltH === null) return TRUMP_SIGN_TOP;
  if (leftHakem)
    return Math.max(
      TRUMP_SIGN_FLOOR,
      Math.min(TRUMP_SIGN_TOP, overChip(feltH, compact, zoom, leftNotice)),
    );
  // a left seat that is not the حاکم moves only when a wrapped notice lifts it half a line (the felt's
  // standing geometry is prod's: at Telegram Desktop 307×472 the avatar has met the sign since card-67)
  if (!leftNotice) return TRUMP_SIGN_TOP;
  // the avatar's ring: its 2 px spread over the root's top, and 2 px of air under the sign
  const overRing = Math.floor(leftRootTop(feltH, compact, zoom, leftNotice) - 2 - 2 - TRUMP_SIGN_H);
  return overRing >= TRUMP_SIGN_TOP || overRing < SIGN_CORNER_TOP ? TRUMP_SIGN_TOP : overRing;
}
/** THE TRUMP SIGN'S LEFT (felt px) — 12 (`left-3`), stepped right only where the left حاکم's chip
 *  raised it into the felt's rounded corner above the floor (see `trumpSignTop`). */
export function trumpSignLeft(
  feltH: number | null,
  compact: boolean,
  zoom: number,
  leftHakem: boolean,
  leftNotice = false,
): number {
  if (feltH === null || !leftHakem) return TRUMP_SIGN_LEFT;
  const top = overChip(feltH, compact, zoom, leftNotice);
  if (top < TRUMP_SIGN_FLOOR || top >= SIGN_CORNER_TOP) return TRUMP_SIGN_LEFT;
  return Math.ceil(cornerReach(top));
}

type BidMove = Extract<ShelemMove, { type: "bid" }>;

/** What a شلم seat looks like — the shared `TableSeat` draws it: the partnership by colour, the gold
 *  turn-glow on the seat to move (the حاکم laying down the کف too), the حاکم's crown chip above its
 *  avatar from the auction's end, the dealer's marker on its corner, its bid or «پاس» under its name
 *  while the auction runs. */
function shelemSeatLook(
  view: ShelemView,
  seat: number,
  /** The hand-end window — or the redeal's — holds the next auction (P1 Task 7; the final review
   *  B-I2): no seat glows as the one to speak. */
  quiet = false,
): Pick<
  TableSeatProps,
  "onTurn" | "ringTone" | "aboveAvatar" | "nameSkin" | "extra" | "avatarCorner"
> {
  const my = onMyTeam(view.you, seat);
  const live = view.phase !== "gameOver";
  const contracted = view.phase === "declaring" || view.phase === "playing";
  return {
    onTurn: live && !quiet && view.turn === seat,
    ringTone: teamRing(my),
    aboveAvatar: contracted && view.declarer === seat ? <HakemChip /> : null,
    nameSkin: seatNameSkin(my),
    extra: view.phase === "bidding" ? <AuctionBadge view={view} seat={seat} /> : null,
    avatarCorner: live && view.dealer === seat ? <DealerMarker label={fa.shelem.dealer} /> : null,
  };
}

/**
 * The trick on the felt — each card in the slot IN FRONT of the seat that played it (you below the
 * centre, the seat after you on your RIGHT, your partner above, the left seat left: the diamond, as
 * on حکم and بی‌دل — P0-7), flying in FROM that seat's edge.
 * THE END OF A TRICK IS ALWAYS SEEN (`hold`, the table's `useTrickHold` — حکم's and بی‌دل's, P1-1): a
 * finished trick stays on the felt for its whole hold + collect from the render where it first
 * appears, whatever newer frames say — a human's instant lead, a bot's, the auction's first word
 * sweeping the carried 12th trick — and the live trick shows only after it. The collect sweeps THIS
 * layer toward the winner's edge; one keyed layer for live and finished cards, so a card carried from
 * `currentTrick` into `completedTrick` keeps its node and never re-flies.
 */
function TrickLayer({
  view,
  hold,
  chip,
  compact,
  zoom,
  felt,
}: {
  view: ShelemView;
  hold: TrickHold;
  /** The collected trick's «+۱۰» (P2-1) — from its collect's start, for `TRICK_POINTS_MS`, when it
   *  held a point card (`useTrickPoints`). */
  chip: { key: string; points: number; mine: boolean } | null;
  compact: boolean;
  /** The page's text zoom (1 = 100 %): the partner's K grows with it (`trickSlots`). */
  zoom: number;
  /** The felt's laid-out size — where it has room, the diamond grows (`shelemTrickScale`). */
  felt: { w: number; h: number } | null;
}): ReactNode {
  const trick = shownTrick(hold.shown);
  const scale = felt ? shelemTrickScale(felt.w, felt.h, compact, zoom) : 1;
  const grown =
    felt && scale > 1 ? grownShelemTrickSlots(felt.w, felt.h, compact, zoom, scale) : null;
  const clamps = trickSlots(compact, zoom);
  const side = grown ? `${grown.side}px` : clamps.side;
  const top = grown ? `${grown.top}px` : clamps.top;
  const drop = grown ? `${grown.drop}px` : clamps.drop;
  const own = grown ? `${grown.own}px` : clamps.own;
  const grow = grown ? ` scale(${scale})` : "";
  const slotFor = (rel: number): CSSProperties => {
    const map: Record<number, CSSProperties> = {
      // you — below the centre
      0: {
        ...atY(`calc(50% + ${drop} + ${own})`),
        left: "50%",
        transform: `translate(-50%,-50%) rotate(-3deg)${grow}`,
      },
      // the seat after you — on your right
      1: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% + ${side})`),
        transform: `translate(-50%,-50%) rotate(4deg)${grow}`,
      },
      // your partner — above the centre
      2: {
        ...atY(`calc(50% + ${drop} - ${top})`),
        left: "50%",
        transform: `translate(-50%,-50%) rotate(3deg)${grow}`,
      },
      // the seat before you — on your left
      3: {
        ...atY(`calc(50% + ${drop})`),
        ...atX(`calc(50% - ${side})`),
        transform: `translate(-50%,-50%) rotate(-4deg)${grow}`,
      },
    };
    return map[rel] ?? map[0]!;
  };
  return (
    <>
      <div
        ref={hold.layerRef}
        className="pointer-events-none absolute inset-0"
        aria-label="trick"
        // for the probes and the tests (no visual change): set while a finished trick is held
        data-trick-holding={hold.holding ? "1" : undefined}
      >
        {trick.map((play) => {
          const rel = (play.seat - view.you + view.seats) % view.seats;
          return (
            <div key={play.seat} className="absolute" style={slotFor(rel)}>
              {/* Flies in FROM THE SEAT THAT PLAYED IT (DIAMOND_SEAT_EDGES); keyed by seat + card, so
                  the entrance fires once per freshly played card. The slot above owns the rest. */}
              <ThrownCard
                key={`${play.seat}-${play.card}`}
                edge={seatEdge(DIAMOND_SEAT_EDGES, rel)}
              >
                <Card id={play.card} large />
              </ThrownCard>
            </div>
          );
        })}
      </div>
      {/* «+۱۰» at the diamond's centre — AFTER the collect's layer, never in it: the cards sweep away
          from under it and it stays, at full opacity (fix 1: riding the layer's fade, a flash) */}
      {chip !== null ? (
        <TrickPointsChip
          key={chip.key}
          points={chip.points}
          mine={chip.mine}
          top={`calc(50% + ${drop})`}
        />
      ) : null}
    </>
  );
}

/**
 * THE HEADER'S LINE UNDER «شلم» — THE HAND NUMBER, AND ONLY THAT (card-67 B1, the operator: «Header:
 * under the game name, only the hand number. Game notices go to the yellow notices at the bottom of
 * the table, like حکم, or to the space between a player's avatar and the timer»). What the line said
 * before went where the operator put it:
 *   «کنار بگذارید» (the حاکم's)    your strip's pill — between your avatar and your timer — says it
 *                                  already («کنار بگذارید (n/۴)»);
 *   «حاکم ۴ کارت کنار می‌ذاره»     the other three's statement: حکم's gold notice at the felt's foot
 *                                  (`declarerNoticeShown`) while a human حاکم's discard window runs;
 *   «پایان دست» / «پایان بازی»     the strip's pill says both, over the felt's hand-end card and the
 *                                  container's end card;
 *   «مزایده» / «دور n از ۱۲»       no notice — the felt's statement and the bidding row ARE the auction,
 *                                  and the trick count is gone with the line («only the hand number»).
 * THE NUMBER: the hands PLAYED (`playedHands` — never `handNumber`, which counts every deal: a redealt
 * hand is the same hand) + 1; while the hand-end window holds (the 12th trick's collect and the hand-end
 * card) the hand that just ENDED — the hand-end frame has already counted it, and what the felt shows
 * is still that hand's; once the match is over, its last hand. Never 0.
 */
export function shelemHandNumber(view: ShelemView, handEnding = false): number {
  const n = handEnding || view.phase === "gameOver" ? view.playedHands : view.playedHands + 1;
  return Math.max(1, n);
}

/**
 * «حاکم ۴ کارت کنار می‌ذاره» — WHEN the other three read it (card-67 B1): in حکم's gold notice at the
 * felt's foot, the table-wide statement the header said before, while the حاکم lays its four aside —
 *   · never to the حاکم itself: its order is its own strip's («کنار بگذارید (n/۴)»);
 *   · not while «{name} زمین رو برداشت» is up (the pickup's line — two statements at once);
 *   · only while the حاکم's DISCARD WINDOW runs (`windowSeat`, the online countdown's seat): the server
 *     arms a window for a HUMAN on turn alone, after the pickup's beat (`primaryOnTurnHuman`,
 *     `scripted-beats-shelem.ts`). A bot lays its four down as that beat ends — 1.5 s, against the
 *     line's 1.3 — so a notice raised between the two would only flash; solo has no countdown, and its
 *     حاکم is you or a bot.
 * Pure, so the rule is pinned for every phase and seat without a socket.
 */
export function declarerNoticeShown({
  view,
  kafLine,
  windowSeat,
}: {
  view: ShelemView;
  /** The pickup's line is up (`useKafMoments`). */
  kafLine: boolean;
  /** Whose turn window the online countdown runs (`timer.turn.seat`), or null. */
  windowSeat: number | null;
}): boolean {
  return (
    view.phase === "declaring" &&
    view.declarer !== null &&
    view.declarer !== view.you &&
    !kafLine &&
    windowSeat === view.declarer
  );
}

/**
 * THE STRIP'S WORDS — true for whoever reads them (W12; card-91's «nothing false on screen»):
 *   your move   «نوبت شماست» — the کف's «کنار بگذارید (n/۴)» to the حاکم laying it down, and «حکم را
 *               انتخاب کنید» to the حاکم about to lead the hand's first card, which names trump (D9);
 *   another's   WHOSE move it is: «نوبت یار» for your partner, «نوبت حریف» for an opponent — never
 *               «منتظر بازیکنان…», which the staging smoke saw on every other seat's turn;
 *   a hand end  «پایان دست» for EVERY reader while the hand-end window holds (the 12th trick's collect
 *               and the hand-end card, P1 Task 7 fix round 1): the next auction waits for the card —
 *               its row, its centre, the opener's glow — so no strip names a turn nobody can take yet
 *               (پاسور's rule: no gold, glow or prompt over a disabled hand);
 *   a redeal    «دوباره پخش» for every reader while the redeal's window holds (D6; the final review
 *               B-I2): the new auction waits for it the same way;
 *   the end     «پایان بازی» — once the match's last hand-end window is over: through its last
 *               collect and hand-end card the strip says «پایان دست», as at every hand's end (the
 *               final review B m1: «پایان بازی» ran ~4 s ahead of the card the player was reading).
 * Pure, so the rule is pinned for every phase and viewer without a layout engine.
 */
export function shelemStatusText({
  view,
  yourMove,
  bidding,
  discarding,
  picks,
  handEnding,
  redealing,
}: {
  view: ShelemView;
  /** The server offers you a move (`legal` is not empty). */
  yourMove: boolean;
  /** …and it is a bid, a pass or the call. */
  bidding: boolean;
  /** …and it is the کف's discard. */
  discarding: boolean;
  /** The cards you have picked for the کف so far. */
  picks: number;
  /** The hand-end window holds (`useHandEndMoments`'s `holding`): the next auction waits for it. */
  handEnding: boolean;
  /** The redeal's window holds (`useRedealMoments`'s `holding`): the new auction waits for it. */
  redealing?: boolean;
}): string {
  if (handEnding) return fa.shelem.handEnd;
  if (view.phase === "gameOver") return fa.shelem.gameOver;
  if (redealing === true) return fa.shelem.redealShort;
  if (bidding) return fa.shelem.yourTurn;
  if (discarding) return `${fa.shelem.discard} (${faDigits(picks)}/${faDigits(WIDOW_SIZE)})`;
  if (yourMove && view.phase === "playing" && view.trump === null) return fa.shelem.chooseTrump;
  if (yourMove || view.turn === view.you) return fa.shelem.yourTurn;
  return onMyTeam(view.you, view.turn) ? fa.shelem.partnerTurn : fa.shelem.opponentTurn;
}

/** Your instruction at the حاکم's first lead, beside your «حاکم» chip — see `useChooseTrumpFit`. */
type ChooseTrumpFit = "long" | "short" | "noChip";

/**
 * YOUR «حاکم» CHIP AND YOUR INSTRUCTION, SIDE BY SIDE (P1 Task 9a fix round 1 — the controller's ruling
 * on the review's design call (b)). The chip stays under your name, where حکم and بی‌دل draw it. While
 * you, the حاکم, are told to choose trump — your first lead: the card you play names it — your name's
 * column is measured against the chip (the twin's, always drawn): where it is narrower (320×568 under
 * a 150 % zoom: 24.6 px for a 40.8 px chip — ring 40 + your name + the pill's 181.4 + the timer's 32
 * and the gaps outrun the strip's 296 px), your pill takes حکم's operator-approved short form «انتخاب
 * حکم» — true in شلم, your first card IS the call; where even that leaves the column short, the chip
 * yields while the instruction lasts (the pill and the HUD's «حاکم: …» name you already). Only the
 * حاکم reads either line. Latched while the instruction lasts — the short pill widens the column, and
 * a re-measure must never bring the long one back — and read in a LAYOUT effect: no frame shows the
 * chip over the pill.
 */
function useChooseTrumpFit(
  active: boolean,
  colRef: { current: HTMLElement | null },
  twinRef: { current: HTMLElement | null },
): ChooseTrumpFit {
  const [fit, setFit] = useState<ChooseTrumpFit>("long");
  useLayoutEffect(() => {
    if (!active) {
      if (fit !== "long") setFit("long");
      return undefined;
    }
    const col = colRef.current;
    const chip = twinRef.current?.lastElementChild;
    if (!col || !(chip instanceof HTMLElement) || fit === "noChip") return undefined;
    // the laid-out widths, UNROUNDED (the final review B m5): `clientWidth` / `offsetWidth` round
    // each side to a whole pixel, so a sub-pixel difference tripped the fallback — or hid a real
    // overflow — by up to a pixel either way; half a pixel of tolerance on the exact widths
    const check = (): void => {
      if (col.getBoundingClientRect().width + 0.5 < chip.getBoundingClientRect().width)
        setFit(fit === "long" ? "short" : "noChip");
    };
    check();
    if (typeof window.ResizeObserver !== "function") return undefined;
    const ro = new window.ResizeObserver(check);
    ro.observe(col);
    return () => ro.disconnect();
  }, [active, fit, colRef, twinRef]);
  return active ? fit : "long";
}

/** What the contract HUD (the bottom bar) and the trump sign draw — latched while the contract is
 *  live, so the hand's end can keep it (B-I1): the frame it read, the points as collected, and the
 *  trump's suit once «حکم: …» has been read (else null) — the felt's top-left sign (card-67 B2). */
interface HudLook {
  readonly view: ShelemView;
  readonly points: readonly number[];
  readonly pip: string | null;
}

export function ShelemTable({
  view,
  legal,
  onMove,
  onLeave,
  self,
  players,
  seatNotices,
  timer,
  onInviteSeat,
  chat = false,
  liftToPlay = getLiftToPlay(),
}: {
  view: ShelemView;
  legal: readonly ShelemMove[];
  onMove: (move: ShelemMove) => void;
  onLeave: () => void;
  /** The signed-in user — renders YOUR seat with your real photo + name. */
  self?: SeatIdentity | null;
  /** The lobby roster: real occupants get their own name, face and verified badge; a bot (or a
   *  human the server could not resolve) keeps the positional stand-in. */
  players?: readonly SeatPlayer[];
  /** Online: what has just happened to each seat, said on that seat (`seat-notice.tsx`). */
  seatNotices?: readonly SeatNotice[] | undefined;
  /** The live turn countdown — online only (null while nobody's turn is timed); solo never passes
   *  it. Drawn in your strip's ONE fixed slot (`TurnTimerSlot`), reserved iff it was passed at all. */
  timer?: TableTurnTimer | null;
  /** Set (online, host, mid-game) so tapping a BOT chair invites a friend into it (the «+»). */
  onInviteSeat?: ((seat: number) => void) | undefined;
  /** Online: the shell's table-chat toggle sits beside the exit on EVERY online table, so its
   *  footprint is kept free from the first frame (`CHAT_TOGGLE_PX`); solo has no chat. */
  chat?: boolean;
  /** «تأیید قبل از بازی کارت» (`game/lift-to-play.ts`), for PLAY only (R7): a card tap LIFTS the
   *  card, a tap on the table (this `<main>`) plays it. The کف's select-four keeps its own
   *  select-then-confirm. Read from the Settings pref by the table itself. */
  liftToPlay?: boolean;
}): ReactNode {
  const you = view.you;
  const seats = view.seats;
  // A zoomed screen (the 384×823 phone, Telegram Desktop): smaller seats and your own avatar.
  const compact = useCompactTable();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tableW = useLaidOut(rootRef, laidOutWidth);
  // The score box's form — measured off the table's width and the page's text zoom, never a
  // breakpoint: see SCORE_EM. Regular until laid out. The zoom is read off the score box's «ما»
  // caption — painted in every form, at every zoom — so a live zoom change resizes it and is re-read
  // (the final review B m6: read off the header's phase line, `hidden` from 105 %, it never was).
  const zoomRef = useRef<HTMLSpanElement | null>(null);
  const zoom = useLaidOut(zoomRef, textZoomOf) ?? 1;
  const scoreForm: ScoreForm = tableW === null ? "regular" : scoreBoxForm(tableW, zoom);
  // …and the hand number under «شلم»: drawn, uncut, wherever its widest label fits beside the box —
  // the header grows for it above 100 % (handLineShown, card-67 B1). Drawn until laid out.
  const handShown = tableW === null || handLineShown(tableW, zoom, scoreForm);
  // `main` — the felt's room under the panel; its laid-out height is read below (`mainH`), once the
  // panel's parts for this render are known.
  const mainRef = useRef<HTMLElement | null>(null);
  // One «play» cue + one send per server frame (repeat taps while the move is in flight are
  // ignored). See useFrameKey / Card.commitKey.
  const frameKey = useFrameKey(view);
  // the host may fill a BOT chair with a friend: the «+» on that seat
  const inviteFor = (seat: number): (() => void) | undefined =>
    onInviteSeat && players?.find((p) => p.seat === seat)?.isBot
      ? () => onInviteSeat(seat)
      : undefined;

  // Names and faces come from ONE place, so a seat reads the same in the chip and the panel.
  const nameOf = (seat: number): string =>
    resolveSeatName(seat, you, self, players, {
      you: fa.shelem.you,
      fallback: (s) => shelemSeatFallback(s, you, seats),
    });
  const avatarOf = (seat: number): SeatAvatar =>
    resolveSeatAvatar(seat, you, self, players, presetAvatarFor);

  // THE END OF A TRICK IS ALWAYS SEEN (P1-1 — the operator, 2026-10-10: «the last card and the full
  // collect animation always shown», as حکم and بی‌دل): the SAME `useTrickHold`. A finished trick stays
  // on the felt for its hold + collect, read under ITS hand's trump — the carried 12th trick under
  // `lastHand`'s (the new deal's `trump` is null until its first card). While it holds, your hand's
  // PLAYS wait (a card played into it would only queue behind the collect); the auction's row and
  // the کف's select-four put no card on the felt and are never held.
  const hold = useTrickHold(view, { game: "shelem", trump: view.trump ?? view.lastHand?.trump });
  /** What the felt SHOWS — the one answer to «is a card on the felt» (the centre and the کف give way). */
  const feltTrick = shownTrick(hold.shown);
  // THE TRICK'S POINTS AND THE HAND'S END (P1 Task 6, P2-1 / P1-8): the «+۱۰» from each collect that
  // takes a point card, for `TRICK_POINTS_MS` (it outlives the collect: fix 1); once the 12th trick is
  // collected, every hand its cue and the hand-end card (below — all twelve wear its gold banner
  // line, `shelem-moments.ts`).
  const trickPoints = useTrickPoints(hold);
  // THE HAND'S END (P1 Task 7): once the 12th trick is collected, the hand-end card — the کف
  // revealed, the contract settled — until it has been read; the next auction (its row, its centre,
  // the face-down کف) waits for it, and the score box keeps the totals before the hand until the
  // card's own entrance. The engine's hand-end beat waits for all of it.
  // It yields to a turn window of YOURS that opens behind it (a reconnect inside the beat: the final
  // review B m7) — never to the one the table already held, your own 12th card's.
  const handEnd = useHandEndMoments(view, feltTrick.length === 0, timer?.turn ?? null);
  // Table sounds: a fresh deal shuffles (bidding phase of each new hand, never a resume) — the کف's
  // four arriving in the حاکم's hand are not a deal (`onGrowth` off while it lays four down: the
  // pickup has its own `widowPickup`); every card another seat lands clicks — the trick's LAST card
  // too; the trick result sounds; and the other seats' bids, passes and call (`shelem-sfx.ts`; your
  // own sound on the tap, below). The card and result cues read what the felt SHOWS (`hold.shown`):
  // a lead held back by the collect clicks when it appears, and the result lands with the sweep.
  useDealSfx(view.hand.length, isVirginDeal(view), { onGrowth: view.phase !== "declaring" });
  useCardLandSfx(lastPlayOf(hold.shown), you);
  useTrickResultSfx(hold.shown, TRICK_HOLD_MS);
  useAuctionSfx(view, you);
  // THE کف AND THE TRUMP CALL (P1 Task 5): the pickup's flight and line, the laid-down sweep, and the
  // first card's announce — each live only when the table saw it arrive (`shelem-moments.ts`), with
  // `widowPickup` / `trumpCall`. While the four fly in, the حاکم's hand is held; while the first card
  // lands and «حکم: …» is read, the next seat's hand is (the engine's beats wait for both).
  const kaf = useKafMoments(view);
  const trump = useTrumpMoments(view);
  const handHeld = kaf.flying || trump.revealing;
  // THE REDEAL (D6; the final review B-I2): seen live, the third «پاس» stays on its seat — your fan
  // keeping the old hand while it is read alone — then «سه پاس؛ دست دوباره پخش می‌شه» and the shuffle
  // as the new hand deals in; the new auction (its row, its centre, the glow) waits for all of it,
  // and so does the engine's redeal beat (`shelem-moments.ts`).
  const redeal = useRedealMoments(view);

  // THE CONTRACT HUD (P1-6) — what it shows while the contract is live, LATCHED during render (an
  // effect would be a render late) so the hand's end can keep it…
  const hudLatch = useRef<HudLook | null>(null);
  const liveHud: HudLook | null =
    view.declarer !== null && (view.phase === "declaring" || view.phase === "playing")
      ? {
          view,
          points: pointsCollected(view, hold),
          // the trump sign only once «حکم: …» has been read
          pip: view.trump !== null && !trump.revealing ? view.trump : null,
        }
      : null;
  if (liveHud !== null) hudLatch.current = liveHud;
  // …THE FELT DOES NOT MOVE UNDER THE 12th TRICK (the final review's B-I1): the hand-end frame deals
  // the next hand — no contract, so no HUD — while that trick is still held and collected, and the
  // panel losing the HUD grew the felt 53.7 px under it: the held cards and the side seats jumped
  // ~27 px as the last card landed, every hand, for every viewer. So the HUD keeps the finished
  // hand's LAST look exactly while its trick is on the felt (`priorTotals` — the match's last trick
  // too), and goes as the hand-end card enters: the felt grows once, with no trick on it, and the
  // card is placed off the grown felt (`mainH`, re-read in that same layout pass) — where Tasks 7
  // and 9a measured it: held through the card, the footprint would drop it to the compact form at
  // 320 ×1.3 and lose the revealed کف. Never a HUD the table did not draw for THIS hand.
  const heldHud: HudLook | null =
    liveHud === null &&
    handEnd.priorTotals &&
    hudLatch.current !== null &&
    hudLatch.current.view.playedHands + 1 === view.playedHands
      ? hudLatch.current
      : null;
  const hud = liveHud ?? heldHud;
  // ── Controls are derived entirely from the authoritative `legal` list ──
  const bidMoves = legal
    .filter((m): m is BidMove => m.type === "bid")
    .map((m) => m.amount)
    .sort((a, b) => a - b);
  const canPass = legal.some((m) => m.type === "pass");
  const canCall = legal.some((m) => m.type === "shelem");
  // …and none of it while the hand-end card is read: the next auction waits for it (P1 Task 7).
  const isBidding =
    !handEnd.holding && !redeal.holding && (bidMoves.length > 0 || canPass || canCall);
  // `main`'s laid-out height — the felt's room under the panel: the auction's centre and the
  // hand-end card are placed off it. Re-read in the layout pass that lets the held HUD go — and in the
  // one that opens or closes your bidding row (card-67 C4: the کف's corner is sized off it, so no
  // frame may read the felt the panel has just changed).
  const panelRev = (heldHud !== null ? 1 : 0) + (isBidding ? 2 : 0);
  const mainH = useLaidOut(mainRef, laidOutHeight, panelRev);
  // …and the felt's own size: where it has room the played cards grow (`shelemTrickScale`, B3 item 1)
  const feltRef = useRef<HTMLDivElement | null>(null);
  const feltW = useLaidOut(feltRef, laidOutWidth, panelRev);
  const feltH = useLaidOut(feltRef, laidOutHeight, panelRev);
  // The auction's centre: placed off `main`'s measured height (the felt's room under the row) —
  // under the side seats where that band holds it, else in the gap under the partner's column.
  // …a side seat's notice wraps onto a second line, so its column reaches lower (Task 9b)
  const sideNotice =
    noticeForSeat(seatNotices, (you + 1) % seats) !== null ||
    noticeForSeat(seatNotices, (you + 3) % seats) !== null;
  const centrePlacement = auctionCentrePlacement(mainH, compact, zoom, sideNotice);
  // the trump sign at حکم's place — clear of the left حاکم's chip on the shortest felts (card-67 D), of
  // the left seat's avatar, and of the felt's rounded corner (card-98)
  const signAt = [
    feltH,
    compact,
    zoom,
    view.declarer === (you + 3) % seats,
    noticeForSeat(seatNotices, (you + 3) % seats) !== null,
  ] as const;
  const signTop = trumpSignTop(...signAt);
  const signLeft = trumpSignLeft(...signAt);
  // THE کف's CORNER (card-67 C4): sized for the felt on YOUR bid turn — the felt now, less the auction
  // panel (and its 8 px gap) whenever the panel is not up — so it is one size through the auction;
  // latched while the auction runs, so the four fly to the حاکم from where they lay
  const kafCorner = kafCornerPlacement(
    feltW,
    feltH === null ? null : feltH - (isBidding ? 0 : auctionPanelHeight(zoom) + PANEL_GAP_PX),
    compact,
    zoom,
    noticeForSeat(seatNotices, (you + 1) % seats) !== null,
  );
  const kafCornerLatch = useRef<KafCorner>(kafCorner);
  if (view.phase === "bidding") kafCornerLatch.current = kafCorner;

  // …and where the felt cannot hold even its tight line, the felt's statement gives way to your
  // bidding row, whose slots name every seat's word — the standing bid gold (B3 item 7; card-67 C3) —
  // the no-bid line too (card-67 D: with your row's slots taller, «هنوز پیشنهادی نیست» was cut by the
  // felt's foot at 320×568 / 300×667 under a 130–150 % zoom and 384×569@1.5 under 150 %; every slot
  // empty and the ladder from ۱۰۰ say it)
  const centreCut = isBidding && !auctionCentreFits(mainH, compact, zoom, sideNotice);
  // The کف: the engine lists ONE sample discard, but any 4 of your cards are legal — so the whole
  // hand is selectable while this move is on offer.
  const discarding = legal.some((m) => m.type === "discard");
  const yourMove = legal.length > 0 && !handEnd.holding && !redeal.holding;
  // The cards a tap may play now — the server's legal plays, and none while a finished trick holds.
  const playable = new Set<string>(
    hold.holding ? [] : legal.flatMap((m) => (m.type === "play" ? [m.card] : [])),
  );

  // «تأیید قبل از بازی کارت» — PLAY only (R7): the کف's select-four keeps its own select-then-confirm,
  // so nothing can be lifted while discarding. Called on every render (the rules of hooks).
  const liftOn = liftToPlay && !discarding;
  const liftPlay = useLiftToPlay({
    enabled: liftOn,
    // inert while the trick holds (as حکم) and while «حکم: …» is read
    holding: trump.revealing || hold.holding,
    frameKey,
    playable,
    onPlay: (card) => onMove({ type: "play", card }),
  });

  // Bidding is a local select-then-confirm interaction (like Hearts' pass flow): tapping a ladder
  // tile selects it, «ثبت» commits the standing selection. The selection defaults to the cheapest
  // legal raise and self-heals if the legal set changes between turns. The شلم call is never
  // preselected.
  const [selectedBid, setSelectedBid] = useState<BidChoice | null>(null);
  // A selection belongs to ONE turn. One table instance lives the whole match, so a «شلم» left
  // selected would open every later auction turn on «ثبت شلم» — one habitual tap from a −330-risk
  // call. Drop it whenever it stops being this seat's bidding turn. Keyed on a BOOLEAN, never on an
  // object off the frame.
  useEffect(() => {
    if (!isBidding) setSelectedBid(null);
  }, [isBidding]);
  const effectiveBid: BidChoice | null =
    selectedBid === "shelem"
      ? canCall
        ? "shelem"
        : (bidMoves[0] ?? null)
      : selectedBid != null && bidMoves.includes(selectedBid)
        ? selectedBid
        : (bidMoves[0] ?? null);
  // The selected tile stays in view: the ladder is a scroller (up to fourteen raises), and a tile
  // tapped at its clipped edge slides in. Keyed on the selection (a number, «shelem» or null).
  const ladderRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const tile = ladderRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (tile && typeof tile.scrollIntoView === "function")
      tile.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [effectiveBid]);
  // ONE move per server frame from the row and the کف's confirm (the card's `commitKey` rule): a tap
  // on «ثبت», «پاس» or «کنار بگذار» sends once and sounds once; a flurry of taps while the move is in
  // flight sends nothing more. The latch opens on the next frame — the move landed, or was refused
  // and the control is live again.
  const sentAt = useRef<number | null>(null);
  const sendOnce = (move: ShelemMove, cue: SfxName | null): void => {
    if (sentAt.current === frameKey) return;
    sentAt.current = frameKey;
    if (cue !== null) playSfx(cue);
    onMove(move);
  };

  // The کف discard is select-then-confirm too (like بی‌دل's pass): a tap toggles a card into the 4,
  // «کنار بگذار» sends them as ONE move. The picks reset whenever the discard is not on offer.
  const [kafPicks, setKafPicks] = useState<readonly string[]>([]);
  useEffect(() => {
    if (!discarding) setKafPicks([]);
  }, [discarding]);
  const picks = discarding ? kafPicks.filter((c) => view.hand.includes(c)) : [];
  const toggleKaf = (card: string): void =>
    setKafPicks((cur) =>
      cur.includes(card)
        ? cur.filter((c) => c !== card)
        : cur.length < WIDOW_SIZE
          ? [...cur, card]
          : cur,
    );

  // Seats relative to you (4-player partnership): partner opposite, opponents flanking — the
  // diamond, as on حکم and بی‌دل: the seat that plays after you (seat+1) on your RIGHT, so play and
  // the deal turn to the right as the راهنما says (P0-7).
  const partnerSeat = (you + 2) % seats;
  const rightSeat = (you + 1) % seats;
  const leftSeat = (you + 3) % seats;
  // …each seat's look; the redeal's three «پاس» stay on their seats through its window — no frame
  // carried the third — and no seat glows as the one to speak while it is read (the final review B-I2)
  const seatView: ShelemView =
    redeal.passedOut.length > 0
      ? { ...view, passed: view.passed.map((_p, seat) => redeal.passedOut.includes(seat)) }
      : view;
  const seatQuiet = handEnd.holding || redeal.holding;

  // Team match scores → the header's box («ما / حریف»), toward the target.
  const myTeam = you % 2;
  // …the totals BEFORE the hand while its 12th trick is still on the felt (P1 Task 7): no hand-end
  // number shows before the hand-end card's own entrance — the box moves WITH the card.
  const prior = handEnd.priorTotals && view.lastHand !== null ? view.lastHand.teamDelta : [0, 0];
  const usScore = (view.teamScores[myTeam] ?? 0) - (prior[myTeam] ?? 0);
  const themScore = (view.teamScores[1 - myTeam] ?? 0) - (prior[1 - myTeam] ?? 0);

  // the header's line under «شلم»: the hand number alone (card-67 B1)
  const handLine = fa.shelem.handOf(faDigits(shelemHandNumber(view, handEnd.holding)));
  const statusText = shelemStatusText({
    view,
    yourMove,
    bidding: isBidding,
    discarding,
    picks: picks.length,
    handEnding: handEnd.holding,
    redealing: redeal.holding,
  });
  // your own «حاکم» chip, under your name, once the auction has made you the حاکم
  const youAreHakem =
    view.declarer === you && (view.phase === "declaring" || view.phase === "playing");
  // …and beside your instruction at your first lead, by measure (Task 9a fix round 1, F4)
  const ownColRef = useRef<HTMLDivElement | null>(null);
  const twinRef = useRef<HTMLSpanElement | null>(null);
  // …and beside your discard instruction while you lay the کف aside (card-a7 B6: at 300×667 under a
  // 150 % zoom «کنار بگذارید (۴/۴)» left your column ~20 px and the chip ran into the pill): the same
  // measure, its short form the count alone («۲/۴»)
  const trumpFit = useChooseTrumpFit(
    youAreHakem && (statusText === fa.shelem.chooseTrump || discarding),
    ownColRef,
    twinRef,
  );
  const ownStatus =
    trumpFit === "long"
      ? statusText
      : discarding
        ? fa.shelem.discardShort(faDigits(picks.length), faDigits(WIDOW_SIZE))
        : fa.shelem.chooseTrumpShort;
  const live = view.phase !== "gameOver";
  const ownName = nameOf(you);
  const ownAvatar = avatarOf(you);
  const ownNameType = compact
    ? "font-body-md text-body-md font-semibold"
    : "font-body-lg text-body-lg";

  // NEW-DEAL identity: the fan is keyed by the deal, so every fresh hand — a played one, or a
  // redeal after three passes (a new dealer) — REMOUNTS every card node and the `deal-card` stagger
  // plays from card 0. Within a deal the key is stable: a play, the کف's four arriving or leaving,
  // never re-deals the cards that stay. While a live redeal's third «پاس» is read alone, the fan
  // keeps the OLD deal (latched during render, as the HUD is): nothing is dealt before the notice.
  const dealLatch = useRef<{ key: string; hand: readonly string[] } | null>(null);
  if (!redeal.passing || dealLatch.current === null)
    dealLatch.current = { key: `${view.playedHands}:${view.dealer}`, hand: view.hand };
  const fanKey = dealLatch.current.key;
  // Your hand: sorted for display (♠♥♣♦, each suit high→low — `hand-sort.ts`), on the shared fan.
  const hand = sortHand(dealLatch.current.hand);
  const n = hand.length;
  const cardScale = HAND_CARD_SCALE;
  const budget = tableW === null ? undefined : handFanBudget(tableW, cardScale, FAN_OFF_CAP);
  const { step, width: fanWidth } = handFanLayout(n, cardScale, budget);

  // max(env, --tg-safe): iOS zeroes env(safe-area-inset-*) in Bot-API-8 fullscreen; the
  // SDK-published --tg-safe-* (device + Telegram chrome) covers that case.
  const topPad = "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))";

  return (
    <div
      ref={rootRef}
      className="relative mx-auto flex w-full max-w-[440px] flex-col overflow-hidden text-on-surface select-none"
      style={{
        height: "var(--game-h)",
        background:
          "radial-gradient(circle at center,rgb(var(--c-room-light)) 0%,rgb(var(--c-surface-dim)) 100%)",
      }}
    >
      {/* HUD — ONE 40 px band at 100 % (above it the hand number's own line grows it: card-67 B1,
          `handLineShown`), from the inline-start end (the right, under rtl): the exit, the chat
          toggle's footprint on an online table, «شلم» over the hand number, the score box. */}
      {/* `data-notice-band`: the band GROWS with the text zoom, so the maintenance warning starts
          below its measured bottom, never on «دست n» (`MaintenanceWarningNotice`, card-98 item 13) */}
      <header data-notice-band="" className="relative z-40 px-4" style={{ paddingTop: topPad }}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={fa.shelem.leave}
            onClick={onLeave}
            // self-start: top-align the exit at the header's paddingTop, on the SAME line as the
            // fixed chat toggle (also pinned at top: 10px+safe)
            className={`${TABLE_BTN} flex h-10 w-10 flex-none self-start items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95`}
          >
            <GameIcon type="exit" size={20} />
          </button>

          {/* `TableChat`'s fixed toggle sits here — not in this flow, so its 40 px are kept free
              (see CHAT_TOGGLE_PX). On every online table, from the first frame; never in solo. */}
          {chat ? (
            <div
              aria-hidden
              data-testid="chat-toggle-slot"
              className="h-10 flex-none"
              style={{ width: CHAT_TOGGLE_PX }}
            />
          ) : null}

          {/* «شلم» and the hand number under it (card-67 B1 — only that: `shelemHandNumber`),
              centred in the space between the score box and the chat toggle (or the exit, solo):
              24 + 16 px — the band's 40 at 100 %, TALLER above it (`min-h-10`: the two lines grow
              with the text zoom, and so does the header — the coordinator's ruling). Neither line is
              ever cut: the title yields to assistive tech where the box leaves it no room
              (titleShown), the hand number where its widest label cannot lean into the gaps beside
              the column (handLineShown). */}
          <div className="pointer-events-none flex min-h-10 min-w-0 flex-1 flex-col items-center justify-center">
            <h1
              className={`m-0 whitespace-nowrap font-headline-md text-headline-md leading-6 text-secondary${tableW === null || titleShown(tableW, zoom) ? "" : " sr-only"}`}
            >
              {fa.shelem.title}
            </h1>
            <span
              className={`m-0 whitespace-nowrap font-caption text-caption text-on-surface-variant${handShown ? "" : " sr-only"}`}
            >
              {handLine}
            </span>
          </div>

          {/* The score box: the target «تا ۱۲۰۰» | «ما» | «حریف» — every value in a FIXED box,
              centred (SCORE_EM), so the box is one width whatever the numbers are; its form
              (regular / compact / tight — the last without the target) measured off the width and
              the zoom (scoreBoxForm). `self-start`: on the band's top line with the exit and the
              fixed chat toggle when the hand number grows the row (card-67 B1). */}
          <div
            aria-label="match-scores"
            className={`flex h-10 flex-none self-start items-center rounded-lg border border-outline-variant bg-surface-container/80 shadow-md ${SCORE_FORM[scoreForm].box}`}
          >
            {SCORE_FORM[scoreForm].target ? (
              <>
                <div className="flex flex-none flex-col items-center leading-none">
                  <span className="m-0 font-caption text-caption text-on-surface-variant">
                    {fa.shelem.toTarget}
                  </span>
                  <span
                    dir="ltr"
                    className={`m-0 inline-block text-center ${SCORE_FORM[scoreForm].value} text-on-surface-variant`}
                    style={{ width: SCORE_EM.target }}
                  >
                    {faDigits(view.targetScore)}
                  </span>
                </div>
                <div className="h-6 w-px bg-outline-variant" />
              </>
            ) : null}
            <div className="flex flex-none flex-col items-center leading-none">
              <span ref={zoomRef} className="m-0 font-caption text-caption text-on-surface-variant">
                {fa.shelem.us}
              </span>
              {/* Key by the value so the number RE-MOUNTS (→ score-bounce) on change. */}
              <span
                key={usScore}
                dir="ltr"
                className={`m-0 inline-block text-center ${SCORE_FORM[scoreForm].value} text-primary`}
                style={{ width: SCORE_EM.total, animation: "score-bounce .5s ease" }}
              >
                {signedScore(usScore)}
              </span>
            </div>
            <div className="h-6 w-px bg-outline-variant" />
            <div className="flex flex-none flex-col items-center leading-none">
              <span className="m-0 font-caption text-caption text-on-surface-variant">
                {fa.shelem.them}
              </span>
              <span
                key={themScore}
                dir="ltr"
                className={`m-0 inline-block text-center ${SCORE_FORM[scoreForm].value} text-error`}
                style={{ width: SCORE_EM.total, animation: "score-bounce .5s ease" }}
              >
                {signedScore(themScore)}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* The felt — with «تأیید قبل از بازی کارت» on, a tap anywhere on it plays the lifted card (a
          tap on a button inside it, the bot seat's «+», stays that button's). */}
      <main
        ref={mainRef}
        className="relative min-h-0 flex-1"
        onClick={liftToPlay ? liftPlay.tapTable : undefined}
      >
        <div
          ref={feltRef}
          data-felt=""
          className="absolute inset-3 overflow-hidden rounded-[40px]"
          style={{
            background:
              "radial-gradient(circle,rgb(var(--c-primary-container)) 0%,rgb(var(--c-felt-deep)) 100%)",
            boxShadow:
              "inset 0 8px 32px rgb(var(--c-felt-shadow) / .8), 0 0 0 6px rgb(var(--c-gold-deep)), 0 12px 48px rgba(0,0,0,.6)",
          }}
        >
          {/* A PLAIN FELT, as حکم's and پاسور's (card-67 B5, the operator: «remove both; plain
              tables like حکم and پاسور, so nothing distracts») — شلم's faint star is gone, and so is
              بی‌دل's heart. */}

          {/* THE TRUMP SIGN (card-67 B2, the operator: «Trump sign: top left, exactly like حکم») —
              حکم's chip (`game/trump-sign.tsx`) where حکم draws it: 12 px in, 24 px down (its top
              row's `inset-x-3 top-6`, the chip at the row's end — the left, under rtl). It reads the
              HUD's latch (`hud.pip`): from the moment «حکم: …» has been read (the announce is the
              FIRST place the suit appears), held through the hand's 12th trick, gone as the
              hand-end card enters. */}
          {hud !== null && hud.pip !== null ? (
            <div
              data-testid="shelem-trump-sign"
              className="absolute left-3 top-6 z-20"
              style={
                signTop !== TRUMP_SIGN_TOP || signLeft !== TRUMP_SIGN_LEFT
                  ? {
                      ...(signTop !== TRUMP_SIGN_TOP ? { top: signTop } : {}),
                      ...(signLeft !== TRUMP_SIGN_LEFT ? { left: signLeft } : {}),
                    }
                  : undefined
              }
            >
              <TrumpSign suit={hud.pip} />
            </div>
          ) : null}

          <TrickLayer
            view={view}
            hold={hold}
            // the 12th trick's «+۱۰» gives way the instant the hand-end card enters (P1 Task 7 fix
            // round 1): the chip is `z-10` and the card `z-30` on the same felt, so the rest of its
            // life ran under the card (at 320 ×1.3 the gap form covers the felt's centre) — and the
            // card states the hand's points. No beat moves: the card still enters at the collect's end
            chip={
              trickPoints !== null && !handEnd.card
                ? {
                    key: trickPoints.key,
                    points: trickPoints.points,
                    mine: onMyTeam(you, trickPoints.winner),
                  }
                : null
            }
            compact={compact}
            zoom={zoom}
            felt={feltW !== null && feltH !== null ? { w: feltW, h: feltH } : null}
          />

          {/* The auction: the face-down کف in the felt's top-right corner (card-67 C4, `kafCorner`;
              beside the partner where the corner cannot hold it), and the one-line statement placed
              off the felt's measured room (AuctionCentre); off the auction, the trump / contract at
              the centre. Both clear while a trick is on the felt. */}
          {view.phase === "bidding" ? (
            handEnd.holding ? null : (
              <>
                {view.widowCount > 0 && feltTrick.length === 0 ? (
                  <WidowCorner count={view.widowCount} corner={kafCorner} />
                ) : null}
                {/* the redeal's notice, in the centre's place, while the new hand deals in */}
                {redeal.notice ? (
                  <RedealNotice placement={centrePlacement} />
                ) : (
                  <AuctionCentre
                    view={view}
                    name={view.highBidder != null ? nameOf(view.highBidder) : null}
                    placement={centrePlacement}
                    // your bidding row's slots carry it where the felt cuts it away (B3 item 7)
                    trickOnFelt={feltTrick.length > 0 || centreCut}
                  />
                )}
              </>
            )
          ) : view.declarer !== null ? (
            <>
              {/* The کف: the other seats' line as the حاکم takes it, in the felt's statement pill;
                  the four flying to the حاکم; then the four laid down going to its pile. */}
              {kaf.line && view.phase === "declaring" && view.declarer !== you ? (
                <KafPickupLine name={nameOf(view.declarer)} placement={centrePlacement} />
              ) : null}
              {/* …then, while a human حاکم lays its four aside, the other three's statement «حاکم ۴
                  کارت کنار می‌ذاره» — the header's line until card-67 B1 — in حکم's gold notice at
                  the felt's foot, as حکم draws its «{name} حاکم شد» (`declarerNoticeShown`). No fill
                  on its entrance: a finished `both` keeps a layer for ever. */}
              {declarerNoticeShown({
                view,
                kafLine: kaf.line,
                windowSeat: timer?.turn.seat ?? null,
              }) ? (
                <div
                  role="status"
                  data-testid="shelem-declarer-notice"
                  className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center"
                >
                  <span style={{ animation: "pop-in-glow .35s ease", display: "block" }}>
                    <HakemNoticePill notice={fa.shelem.declarerDiscards(faDigits(WIDOW_SIZE))} />
                  </span>
                </div>
              ) : null}
              {kaf.flying ? (
                <KafPickupFlight
                  rel={(view.declarer - you + seats) % seats}
                  kaf={kaf.kaf}
                  corner={kafCornerLatch.current}
                />
              ) : null}
              {kaf.sweeping ? (
                <KafDiscardSweep rel={(view.declarer - you + seats) % seats} />
              ) : null}
              {/* The trump call: «حکم: ♠ — {name}» once the حاکم's first card has landed — under
                  your partner when YOU led it, at the felt's foot when another seat did. */}
              {trump.announce && view.trump !== null ? (
                <TrumpAnnounce
                  pip={trumpPip(view.trump)}
                  name={nameOf(view.declarer)}
                  at={view.declarer === you ? "top" : "bottom"}
                  top={`${(PARTNER_TOP_IN_FELT + seatRootHeight(compact, zoom) + 4).toFixed(1)}px`}
                />
              ) : null}
            </>
          ) : null}

          {/* THE HAND-END CARD (P1-7, P1 Task 7; card-67 C5): once the 12th trick is collected —
              how the contract went (all twelve wear the hand's gold banner), the حاکم and the four
              it laid down, revealed, and each team's points this hand (the target where the header
              has none; the totals are the header's) — in the felt's middle, placed off `main`'s
              measured height. */}
          {handEnd.card && view.lastHand !== null ? (
            <HandEndCard
              last={view.lastHand}
              you={you}
              targetScore={view.targetScore}
              hakemName={nameOf(view.lastHand.declarer)}
              placement={handEndCardPlacement(
                mainH,
                compact,
                zoom,
                allTwelve(view.lastHand),
                !SCORE_FORM[scoreForm].target,
              )}
            />
          ) : null}
        </div>

        {/* Seats: your partner top, the opponents right and left — حکم's places: the partner at
            38 px, low enough that a «حاکم» chip above it clears the felt's gold ring; the
            opponents 16 px past the safe-area inset, their names capped short of the side cards. */}
        <TableSeat
          {...shelemSeatLook(seatView, partnerSeat, seatQuiet)}
          name={nameOf(partnerSeat)}
          avatar={avatarOf(partnerSeat)}
          posClass="top-[38px] left-1/2 -translate-x-1/2"
          onInvite={inviteFor(partnerSeat)}
          notice={noticeForSeat(seatNotices, partnerSeat)}
          compact={compact}
          nameMax={TOP_NAME_MAX}
        />
        <ShelemSideSeat
          {...shelemSeatLook(seatView, rightSeat, seatQuiet)}
          name={nameOf(rightSeat)}
          avatar={avatarOf(rightSeat)}
          posClass="top-[50%] right-[calc(max(env(safe-area-inset-right),var(--tg-safe-right,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(rightSeat)}
          notice={noticeForSeat(seatNotices, rightSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
        />
        <ShelemSideSeat
          {...shelemSeatLook(seatView, leftSeat, seatQuiet)}
          name={nameOf(leftSeat)}
          avatar={avatarOf(leftSeat)}
          posClass="top-[50%] left-[calc(max(env(safe-area-inset-left),var(--tg-safe-left,0px))_+_16px)] -translate-y-1/2"
          onInvite={inviteFor(leftSeat)}
          notice={noticeForSeat(seatNotices, leftSeat)}
          compact={compact}
          nameMax={SIDE_NAME_MAX}
          wrapNotice
        />
      </main>

      {/* Your panel: a phase's own controls ABOVE your strip (the strip and the timer in it do not
          move when they open), then your strip, then your hand. */}
      {/* `min-h-0` (card-67 D): where even an empty felt leaves your bidding row no room (Telegram
          Desktop's 256×393 under a 130–150 % text zoom, the felt at 0 px) the PANEL gives — the row
          yields its top (`AuctionPanel`) — never the strip and the hand, which keep their one place. */}
      <div
        className="relative z-30 flex flex-col gap-2 min-h-0 bg-gradient-to-t from-surface-container-lowest via-surface-container-lowest/80 to-transparent px-3 pt-2"
        style={{ paddingBottom: "calc(14px + var(--game-safe-bottom))" }}
      >
        {/* THE AUCTION PANEL (card-67 C1 / C3, `shelem-auction.tsx`) — yours alone, ABOVE the strip
            (the strip and the timer in it never move): your BIDDING ROW off the server's legal list
            — the seats' slots, the ladder, «پاس» and «ثبت». */}
        {isBidding ? (
          <BidRow
            view={view}
            nameOf={nameOf}
            avatarOf={avatarOf}
            bidMoves={bidMoves}
            effectiveBid={effectiveBid}
            canCall={canCall}
            canPass={canPass}
            ladderRef={ladderRef}
            onSelect={setSelectedBid}
            onSend={sendOnce}
          />
        ) : null}

        {/* THE BOTTOM BAR (the contract HUD) — once the auction has made a contract, where the
            bidding row stood: the contract and our points this hand, one line of fixed slots
            (`shelem-contract.tsx`, card-67 B3) — and the target where the header's box has none
            (its tight form). Held, as it last read, while the hand's 12th trick is still on the
            felt (`heldHud`, B-I1). The حاکم's too, while it lays the کف aside. */}
        {hud !== null ? (
          <ContractHud
            view={hud.view}
            points={hud.points}
            showTarget={!SCORE_FORM[scoreForm].target}
            short={hudShortForm(tableW, zoom)}
          />
        ) : null}

        {/* The حاکم's کف (R7: never lift-to-play): pick 4 in the fan — the four from the کف marked —
            then «کنار بگذار» lays them down as ONE move, once per frame; shut while the four fly in.
            card-67 C2, the operator: «The «کنار بگذار» box: smaller, so the table doesn't jump
            between bidding and discarding and the cards below have more room to pick from» — ONE
            button at «ثبت»'s size (an `h-9` button in body-lg, not the 56 px headline button), under
            the bar: the felt gives the bar and this row alone (74 / 78 / 81 px at 384×823 ×1 / ×1.3
            / ×1.5, C's estimate; 94 / 98 / 101 before card-67). */}
        {discarding ? (
          <button
            type="button"
            disabled={picks.length !== WIDOW_SIZE || kaf.flying}
            onClick={() => sendOnce({ type: "discard", cards: [...picks] }, null)}
            className={`${TABLE_BTN} flex h-9 items-center justify-center rounded-lg border-0 bg-secondary font-body-lg text-body-lg text-ink-on-gold transition-transform enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-50`}
            // the gold glow only on a button that can be pressed (B3 item 3, operator 2026-10-20):
            // while it waits for the four, a disabled button's glow lay over the top of your avatar
            // below it — «a light halo covers the top of a player's profile and changes its colour»;
            // and a TIGHT one (card-a7 B6): `0 4px 14px` reached 18 px down, across the gap to your
            // ring — this one stops 8 px down, as your ring's own glow stops 8 px up (`OWN_TURN_GLOW`)
            style={
              picks.length === WIDOW_SIZE && !kaf.flying
                ? { boxShadow: "0 2px 6px rgba(232,194,104,.4)" }
                : undefined
            }
          >
            {fa.shelem.discardConfirm}
          </button>
        ) : null}

        {/* YOUR STRIP — avatar, name (and your «حاکم» chip under it), then the status pill and the
            timer's ONE slot LAST: the strip's physical left end under rtl, opposite your avatar.
            `min-w-0` down the name's side lets a long name truncate; the pill never wraps.
            A height FLOOR — your ring's 42 px, the tallest the row draws at 100 % — plus an invisible
            zero-width TWIN of your name over your chip (بی‌دل's points twin): under a WebView text
            zoom that column outgrows the floor, and the twin keeps the row that tall whether you
            are the حاکم or not, so the row's centre — your avatar's and the timer's line — never
            moves. */}
        <div
          data-testid="own-strip"
          className="flex min-h-[42px] shrink-0 items-center justify-between gap-2"
        >
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="relative flex items-center justify-center rounded-full"
              style={{
                padding: 2,
                // border-box + flex centre: the 2px ring padding never pushes the (block-level)
                // avatar out of its box on Android — same guard as the seats.
                boxSizing: "border-box",
                // you anchor your team → green ring; the gold turn-glow wins on your move — TIGHT
                // here (`OWN_TURN_GLOW`): it never reaches the controls above the strip
                // (card-12: it re-pulses on EVERY turn of yours; steady, no settle, while a
                // finished trick is held — your lead re-pulses when the hand is live)
                boxShadow: yourMove
                  ? hold.holding
                    ? TURN_GLOW_STEADY_TIGHT
                    : OWN_TURN_GLOW
                  : "0 0 0 2px rgb(var(--c-primary))",
                animation:
                  yourMove && !hold.holding
                    ? turnGlowSettle(view.phase, view.trickNumber, true)
                    : undefined,
              }}
            >
              <Avatar
                avatarId={ownAvatar.avatarId}
                avatarPhotoId={ownAvatar.avatarPhotoId}
                size={compact ? OWN_AVATAR.compact : OWN_AVATAR.regular}
                official={ownAvatar.official}
              />
              {live && view.dealer === you ? (
                <div className={AVATAR_CORNER}>
                  <DealerMarker label={fa.shelem.dealer} />
                </div>
              ) : null}
            </div>
            <div
              ref={ownColRef}
              className="flex min-w-0 flex-col items-start gap-0.5 leading-tight"
            >
              {/* `min(168px, 100%)`: a nowrap name's min-content IS its full width, so a plain cap
                  never lets it shrink when the row runs short. `dir="auto"` keeps a Latin name's
                  START (W16). */}
              <span
                dir="auto"
                className={`m-0 inline-block truncate ${ownNameType} text-primary`}
                style={{ maxWidth: "min(168px, 100%)" }}
              >
                {ownName}
              </span>
              {youAreHakem && trumpFit !== "noChip" ? <HakemChip /> : null}
            </div>
            <span
              ref={twinRef}
              aria-hidden="true"
              data-strip-twin=""
              className="invisible -ms-2 flex w-0 flex-col items-start gap-0.5 overflow-hidden leading-tight"
            >
              <span className={`m-0 inline-block ${ownNameType}`}>{"\u00a0"}</span>
              <HakemChip />
            </span>
          </div>
          <div className="flex flex-none items-center gap-1.5">
            <StatusPill gold={yourMove}>{ownStatus}</StatusPill>
            {/* THE TURN TIMER'S ONE FIXED SLOT (W3) — the LAST child of this group: online, from the
                first frame (empty while nobody's turn is timed); never in solo. */}
            <TurnTimerSlot timer={timer} />
          </div>
        </div>

        {/* YOUR HAND — face-up, sorted, fitted to the screen (FAN_OFF_CAP, `handFanBudget`); only
            the server's legal plays are tappable (raised + the gold pulse) and nothing is dimmed.
            HELD (nothing tappable, nothing raised) while the کف's four fly in and while the first
            card lands and «حکم: …» is read. The کف's four in YOUR sixteen wear a gold mark. */}
        <div
          key={fanKey}
          aria-label="your hand"
          className="flex w-full shrink-0 justify-center overflow-visible px-2"
        >
          <div
            className="relative"
            style={{ height: HAND_FAN_BOX_H, width: fanWidth, maxWidth: "100%" }}
          >
            {hand.map((card, i) => {
              const legalCard = playable.has(card) && !handHeld;
              const picked = picks.includes(card);
              // While discarding every card is selectable until 4 are picked; a picked one stays
              // tappable (to put it back) — once the four from the کف have landed.
              const tappable = discarding
                ? !handHeld && (picked || picks.length < WIDOW_SIZE)
                : legalCard;
              // «تأیید قبل از بازی کارت»: the lifted card stands LIFTED_EXTRA_PX over the legal rise,
              // and while one is lifted the other legal cards rest at 0 (and stop pulsing).
              const isLifted = liftPlay.lifted === card;
              const rise = discarding
                ? picked
                  ? 30
                  : tappable
                    ? 18
                    : 0
                : isLifted
                  ? 18 + LIFTED_EXTRA_PX
                  : legalCard && liftPlay.lifted === null
                    ? 18
                    : 0;
              return (
                <div
                  key={card}
                  className="absolute bottom-0"
                  style={{
                    // its place on the fan's arc, `rise` px over it (`hand-fan.ts`)
                    ...handCardPlacement(i, n, step, rise, cardScale, FAN_OFF_CAP),
                    zIndex: i,
                    // the lifted card rises on an easing that never overshoots (LIFTED_TRANSITION)
                    transition: isLifted
                      ? LIFTED_TRANSITION
                      : "transform .2s cubic-bezier(.175,.885,.32,1.275)",
                  }}
                >
                  {/* a card from the کف: a small gold mark in the card's BOTTOM-LEFT corner — it
                      tells the حاکم which four of the sixteen came up from the کف, and it rides the
                      card's rise and the fan's tilt. B3 item 4 (operator 2026-10-20: «today it
                      covers the number»): at `-top-1.5` it lay on the corner index; above the card
                      it met your strip's pill once a card rose; and under the classic deck's suit
                      pip it lay on the LARGE index the مدرن / پارسه / سلطنتی decks draw (to y 27–35
                      of 70). The bottom-left corner holds no index on any deck (the second one is
                      bottom-RIGHT, turned), and every deck's faces, scanned, leave the mark's box —
                      x 1.5–8.5, y 62.5–69.5 of the 50 × 70 card, its ring included — blank: the
                      classic court frame ends at y 62.2, its pips' column starts at x 8.4. Inside
                      the strip the fan leaves showing, inside the card's rounded corner */}
                  {kaf.kaf.includes(card) ? (
                    <span
                      data-testid="shelem-kaf-mark"
                      role="img"
                      aria-label={fa.shelem.widow}
                      className="pointer-events-none absolute z-10 rounded-full bg-secondary"
                      style={{
                        left: 2.5,
                        top: 63.5,
                        width: 5,
                        height: 5,
                        boxShadow: "0 0 0 1px rgb(var(--c-surface-container-lowest))",
                      }}
                    />
                  ) : null}
                  {discarding ? (
                    // A selection tap: no commitKey (instant, local, tapped repeatedly).
                    <Card
                      id={card}
                      large
                      dealIndex={i}
                      selected={picked}
                      disabled={!tappable}
                      onClick={() => toggleKaf(card)}
                    />
                  ) : liftOn ? (
                    // the hook owns the latch and the one «play» cue (on the table tap): the card is
                    // silent and carries no commitKey
                    <Card
                      id={card}
                      large
                      dealIndex={i}
                      disabled={!legalCard}
                      silent
                      selected={isLifted}
                      lifted={isLifted}
                      resting={liftPlay.lifted !== null && !isLifted}
                      onClick={() => liftPlay.tapCard(card)}
                    />
                  ) : (
                    <Card
                      id={card}
                      large
                      dealIndex={i}
                      disabled={!legalCard}
                      commitKey={frameKey}
                      onClick={() => onMove({ type: "play", card })}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
